import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMechanicAuth } from '../MechanicAuthContext';
import axios from 'axios';
import { Wrench, ClipboardCheck, AlertTriangle, Plus, ChevronRight, User } from 'lucide-react';
import { toast } from 'sonner';
import FlaggedInspectionCard from '../../components/FlaggedInspectionCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = { open: 'Ouvert', in_progress: 'En cours', completed: 'Complete' };
const STATUS_COLORS = {
  open: 'bg-blue-500/15 text-blue-400',
  in_progress: 'bg-amber-500/15 text-amber-400',
  completed: 'bg-emerald-500/15 text-emerald-400',
};

export default function MechanicHomePage() {
  const { token, user } = useMechanicAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const dashRes = await axios.get(`${API_URL}/api/mechanic/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(dashRes.data);
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const recentOrders = data?.recent_orders || [];
  const flaggedInspections = data?.flagged_inspections || [];

  return (
    <div className="p-4 pb-20 space-y-5" data-testid="mechanic-home">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400 text-sm">Bonjour</p>
          <h1 className="text-xl font-bold text-white" data-testid="mechanic-home-name">
            {user?.first_name} {user?.last_name}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/admin/mecanicien/profil')}
            className="w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-amber-400 transition-colors"
            aria-label="Profil"
            data-testid="profile-btn"
          >
            <User size={18} />
          </button>
          <button
            onClick={() => navigate('/admin/mecanicien/bons/nouveau')}
            className="bg-amber-500 hover:bg-amber-400 text-black font-semibold px-4 py-2.5 rounded-xl flex items-center gap-2 transition-colors text-sm"
            data-testid="new-work-order-btn"
          >
            <Plus size={18} /> Nouveau
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <button onClick={() => navigate('/admin/mecanicien/bons?status=open')} className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-left">
          <Wrench size={20} className="text-blue-400 mb-1" />
          <p className="text-2xl font-bold text-white">{stats.open_orders || 0}</p>
          <p className="text-[10px] text-blue-400/70">Ouverts</p>
        </button>
        <button onClick={() => navigate('/admin/mecanicien/bons?status=completed')} className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-left">
          <ClipboardCheck size={20} className="text-emerald-400 mb-1" />
          <p className="text-2xl font-bold text-white">{stats.completed_today || 0}</p>
          <p className="text-[10px] text-emerald-400/70">Aujourd'hui</p>
        </button>
        <button onClick={() => navigate('/admin/mecanicien/inspections')} className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-left">
          <AlertTriangle size={20} className="text-red-400 mb-1" />
          <p className="text-2xl font-bold text-white">{flaggedInspections.length}</p>
          <p className="text-[10px] text-red-400/70">Defauts</p>
        </button>
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-white">Bons recents</h2>
          <button onClick={() => navigate('/admin/mecanicien/bons')} className="text-amber-400 text-xs font-medium flex items-center gap-1">
            Voir tout <ChevronRight size={14} />
          </button>
        </div>
        {recentOrders.length === 0 ? (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
            <Wrench size={32} className="mx-auto text-slate-600 mb-2" />
            <p className="text-slate-500 text-sm">Aucun bon de travail</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recentOrders.slice(0, 5).map(order => (
              <button
                key={order.id}
                onClick={() => navigate(`/admin/mecanicien/bons/${order.id}`)}
                className="w-full bg-slate-900 border border-slate-800 rounded-xl p-4 text-left active:scale-[0.98] transition-transform"
                data-testid={`order-${order.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-semibold text-sm">{order.order_number}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[order.status] || 'bg-slate-500/15 text-slate-400'}`}>
                    {STATUS_LABELS[order.status] || order.status}
                  </span>
                </div>
                <div className="flex gap-3 text-xs text-slate-400">
                  {order.vehicle_unit && <span>#{order.vehicle_unit}</span>}
                  {order.vehicle_plate && <span>{order.vehicle_plate}</span>}
                  {order.client_name && <span>{order.client_name}</span>}
                </div>
                {order.created_by_name && (
                  <p className="text-[10px] text-slate-500 mt-1" data-testid={`order-${order.id}-by`}>
                    Fait par: {order.created_by_name}
                  </p>
                )}
                {(order.tasks || []).length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">{order.tasks.length} tache{order.tasks.length > 1 ? 's' : ''}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {flaggedInspections.length > 0 && (
        <section>
          <h2 className="text-base font-semibold text-white mb-3">Inspections avec defauts</h2>
          <div className="space-y-2">
            {flaggedInspections.map((insp, i) => (
              <FlaggedInspectionCard
                key={insp.id || i}
                inspection={insp}
                testIdPrefix="home-flagged"
                onCreateWorkOrder={(ins) => navigate(`/admin/mecanicien/bons/nouveau?from_inspection=${ins.id}`)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
